// Reports — quick analytics dashboard. Cheap to render: one GET hits
// /api/reports/summary which runs four small aggregations server-side.
// All numbers are store-scoped to whatever LocationSwitcher last picked.

import { useEffect, useState } from "react";
import { apiGet } from "../lib/api";
import type { ReportsSummary } from "@shared/types";

const STORAGE_STORE_KEY = "smartrx-entry.currentStoreCode";

function getCurrentStoreCode(): string {
  return (typeof window !== "undefined" && window.localStorage.getItem(STORAGE_STORE_KEY)) || "GV";
}

const STATUS_ORDER: { key: string; label: string }[] = [
  { key: "matched",        label: "Pending review" },
  { key: "tech_review",    label: "In review" },
  { key: "on_hold",        label: "On hold" },
  { key: "imported_to_pk", label: "Filed to PK" },
  { key: "rejected",       label: "Rejected" },
];

export function Reports() {
  const [data, setData] = useState<ReportsSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const storeCode = getCurrentStoreCode();

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    apiGet<ReportsSummary>(`/reports/summary?store=${storeCode}`, ctrl.signal)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => { if (!ctrl.signal.aborted) { setData(null); setError(String(e)); } })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
  }, [storeCode]);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="text-2xl font-semibold">Dashboard</h2>
        <span className="text-ink-soft text-sm">{storeCode}</span>
      </div>
      <p className="text-ink-soft mb-6">
        {error
          ? <span className="text-bad">Reports unavailable: {error}</span>
          : "Throughput and accuracy for this store, rolling windows."}
      </p>

      {loading && !data && (
        <p className="text-ink-soft text-sm py-6 text-center">Loading…</p>
      )}

      {data && (
        <>
          <h3 className="text-xs uppercase tracking-wide text-ink-soft mb-2">Last 7 days · by status</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            {STATUS_ORDER.map((s) => (
              <Counter key={s.key} label={s.label} value={data.statusCounts7d[s.key] ?? 0} />
            ))}
          </div>

          <h3 className="text-xs uppercase tracking-wide text-ink-soft mb-2">Last 30 days · by status</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
            {STATUS_ORDER.map((s) => (
              <Counter key={s.key} label={s.label} value={data.statusCounts30d[s.key] ?? 0} muted />
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-surface border border-border rounded-lg p-4">
              <h3 className="text-xs uppercase tracking-wide text-ink-soft mb-3">AI extraction accuracy · 30d</h3>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-3xl font-semibold">{data.aiAccuracy.accuracyPct.toFixed(1)}%</span>
                <span className="text-ink-soft text-sm">fields correct as extracted</span>
              </div>
              <p className="text-ink-soft text-xs">
                {data.aiAccuracy.rxReviewed} Rx reviewed · {data.aiAccuracy.fieldsCorrected} of {data.aiAccuracy.fieldsTotal} fields needed a reviewer correction.
              </p>
            </div>

            <div className="bg-surface border border-border rounded-lg p-4">
              <h3 className="text-xs uppercase tracking-wide text-ink-soft mb-3">Average review time · 30d</h3>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-3xl font-semibold">
                  {data.avgReviewSeconds == null ? "—" : formatDuration(data.avgReviewSeconds)}
                </span>
                <span className="text-ink-soft text-sm">intake to approved</span>
              </div>
              <p className="text-ink-soft text-xs">
                Measured from when the document arrived to when a reviewer marked the Rx approved.
              </p>
            </div>
          </div>

          <div className="bg-surface border border-border rounded-lg p-4">
            <h3 className="text-xs uppercase tracking-wide text-ink-soft mb-3">Throughput · Rx filed per day (14d)</h3>
            <Bars data={data.throughput} />
          </div>
        </>
      )}
    </div>
  );
}

function Counter({ label, value, muted = false }: { label: string; value: number; muted?: boolean }) {
  return (
    <div className={`bg-surface border border-border rounded-lg p-4 ${muted ? "opacity-90" : ""}`}>
      <div className="text-xs uppercase tracking-wide text-ink-soft mb-1">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
    </div>
  );
}

// Fills in any missing days in the last 14 with zeros so the bar chart has a
// stable x-axis even when filings are sparse.
function Bars({ data }: { data: { date: string; count: number }[] }) {
  const today = new Date();
  const days: { date: string; count: number }[] = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const found = data.find((r) => r.date === key);
    days.push({ date: key, count: found?.count ?? 0 });
  }
  const max = Math.max(1, ...days.map((d) => d.count));

  return (
    <div className="flex items-end gap-2 h-40">
      {days.map((d) => {
        const heightPct = (d.count / max) * 100;
        return (
          <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${d.date}: ${d.count}`}>
            <span className="text-[10px] text-ink-soft">{d.count || ""}</span>
            <div
              className="w-full bg-accent rounded-t"
              style={{ height: `${Math.max(heightPct, d.count > 0 ? 4 : 0)}%`, minHeight: d.count > 0 ? 4 : 0 }}
            />
            <span className="text-[10px] text-ink-soft">{d.date.slice(5)}</span>
          </div>
        );
      })}
    </div>
  );
}

function formatDuration(sec: number): string {
  if (sec < 60)   return `${Math.round(sec)}s`;
  const m = sec / 60;
  if (m < 60)     return `${m.toFixed(1)} min`;
  const h = m / 60;
  if (h < 48)     return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} d`;
}
